import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Pencil, Trash2, Clock, Users } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const EMPTY = { name: "", description: "", duration: 60, capacity: 10, active: true };

export default function Services() {
  const [items, setItems] = useState(null);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [delId, setDelId] = useState(null);

  const load = () => api.get("/services").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/branches").then((r) => setBranches(r.data)); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name, description: s.description || "", duration: s.duration, capacity: s.capacity, active: s.active }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, duration: Number(form.duration), capacity: Number(form.capacity), branch_id: (editing?.branch_id) || branches[0]?.id };
      if (editing) { await api.put(`/services/${editing.id}`, payload); toast.success("Hizmet güncellendi"); }
      else { await api.post("/services", payload); toast.success("Hizmet oluşturuldu"); }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    try { await api.delete(`/services/${delId}`); toast.success("Hizmet silindi"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setDelId(null); }
  };

  return (
    <div data-testid="admin-services-page">
      <PageHeader title="Hizmetler" subtitle={`${items?.length ?? "—"} hizmet tanımlı`}
        action={<Button onClick={openNew} data-testid="add-new-service-button" className="bg-[#FF5500] hover:bg-[#FF661A] text-white font-bold rounded-xl"><Plus size={18} className="mr-1" /> Yeni Hizmet</Button>} />

      {items === null ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-36 rounded-2xl bg-[#13131C]" />)}</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((s) => (
            <div key={s.id} data-testid={`service-card-${s.id}`} className="rounded-2xl bg-[#13131C] border border-[#222230] p-5 hover:border-[#FF5500]/40 transition-all group">
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-display font-bold text-white text-lg">{s.name}</h3>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => openEdit(s)} data-testid={`edit-service-${s.id}`} className="p-1.5 rounded-lg text-[#68687B] hover:text-[#FF5500] hover:bg-[#252536]"><Pencil size={15} /></button>
                  <button onClick={() => setDelId(s.id)} data-testid={`delete-service-${s.id}`} className="p-1.5 rounded-lg text-[#68687B] hover:text-[#EF4444] hover:bg-[#252536]"><Trash2 size={15} /></button>
                </div>
              </div>
              <p className="text-sm text-[#A0A0B2] line-clamp-2 min-h-[40px]">{s.description}</p>
              <div className="flex items-center gap-4 mt-4 pt-4 border-t border-[#1A1A24] text-sm text-[#A0A0B2]">
                <span className="flex items-center gap-1.5"><Clock size={15} /> {s.duration} dk</span>
                <span className="flex items-center gap-1.5"><Users size={15} /> {s.capacity} kişi</span>
                <span className={`ml-auto text-[11px] font-bold uppercase px-2 py-0.5 rounded-full border ${s.active ? "bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30" : "bg-[#68687B]/15 text-[#A0A0B2] border-[#68687B]/30"}`}>{s.active ? "Aktif" : "Pasif"}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#13131C] border-[#222230] text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Hizmet Düzenle" : "Yeni Hizmet"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><label className="text-xs text-[#A0A0B2] mb-1 block">Hizmet Adı</label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="service-form-name" className="bg-[#0E0E14] border-[#262636] text-white" /></div>
            <div><label className="text-xs text-[#A0A0B2] mb-1 block">Açıklama</label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-[#0E0E14] border-[#262636] text-white" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-[#A0A0B2] mb-1 block">Süre (dk)</label><Input type="number" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className="bg-[#0E0E14] border-[#262636] text-white" /></div>
              <div><label className="text-xs text-[#A0A0B2] mb-1 block">Kapasite</label><Input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className="bg-[#0E0E14] border-[#262636] text-white" /></div>
            </div>
            <div className="flex items-center justify-between rounded-xl bg-[#0E0E14] border border-[#262636] px-4 py-3">
              <span className="text-sm text-white">Aktif</span>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="service-form-active" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-[#A0A0B2]">İptal</Button>
            <Button onClick={save} disabled={saving || !form.name} data-testid="service-form-submit" className="bg-[#FF5500] hover:bg-[#FF661A] text-white font-bold">{saving ? <Loader2 className="animate-spin" size={16} /> : "Kaydet"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={() => setDelId(null)}>
        <AlertDialogContent className="bg-[#13131C] border-[#222230] text-white">
          <AlertDialogHeader><AlertDialogTitle>Hizmeti sil?</AlertDialogTitle><AlertDialogDescription className="text-[#A0A0B2]">Bu işlem geri alınamaz.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#1B1B27] border-[#2D2D3F] text-white hover:bg-[#252536]">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-service" className="bg-[#EF4444] hover:bg-[#dc2626] text-white">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
